import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Network, TrainFront, Building2, Sprout, FileText,
  Landmark, Shield, Layers, ArrowRight,
} from "lucide-react";
import { useI18n } from "../i18n";

export const Programme = () => {
  const { t } = useI18n();
  const CHANTIERS = [
    { icon: Network, n: "01", tk: "prog.c1t", dk: "prog.c1d", span: "lg:col-span-2" },
    { icon: TrainFront, n: "02", tk: "prog.c2t", dk: "prog.c2d" },
    { icon: Building2, n: "03", tk: "prog.c3t", dk: "prog.c3d" },
    { icon: Sprout, n: "04", tk: "prog.c4t", dk: "prog.c4d" },
    { icon: FileText, n: "05", tk: "prog.c5t", dk: "prog.c5d", span: "lg:col-span-2" },
    { icon: Landmark, n: "06", tk: "prog.c6t", dk: "prog.c6d" },
    { icon: Shield, n: "07", tk: "prog.c7t", dk: "prog.c7d" },
    { icon: Layers, n: "08", tk: "prog.c8t", dk: "prog.c8d" },
  ];

  return (
    <section id="programme" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 flex items-center pointer-events-none select-none opacity-[0.03]">
        <Marquee speed={40} autoFill>
          <span className="font-display font-black text-[10rem] tracking-tighter text-[#0B1121] px-8">
            {t("prog.marquee")}&nbsp;
          </span>
        </Marquee>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="max-w-2xl mb-16">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E1000F] mb-4">
            {t("prog.eyebrow")}
          </p>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-[#0B1121] leading-[0.95]">
            {t("prog.title")}
          </h2>
          <p className="mt-6 text-lg text-slate-600">{t("prog.desc")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {CHANTIERS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.n}
                data-testid={`programme-card-${c.n}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`group bg-white border border-slate-200 rounded-2xl p-7 hover:border-[#001489] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${c.span || ""}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-xl bg-[#001489]/5 flex items-center justify-center group-hover:bg-[#001489] transition-colors">
                    <Icon className="text-[#001489] group-hover:text-white transition-colors" size={22} />
                  </div>
                  <span className="font-mono-accent text-sm text-slate-300">{c.n}</span>
                </div>
                <h3 className="mt-5 font-display font-extrabold tracking-tight text-xl text-[#0B1121]">{t(c.tk)}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{t(c.dk)}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12">
          <Link
            to="/programme"
            data-testid="programme-full-link"
            className="group inline-flex items-center gap-2 bg-[#0B1121] text-white font-semibold px-7 py-4 rounded-full hover:bg-[#001489] transition-all hover:scale-[1.02]"
          >
            {t("prog.cta")}
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};
